import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Package, History, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useB2BAuth } from '../../context/B2BAuthContext';
import { businessTypes } from '../../data/b2bData';
import { bakeryInfo } from '../../data/mock';

const B2BDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useB2BAuth();

  // Redirect if not authenticated
  React.useEffect(() => {
    if (!user) {
      navigate('/b2b/login');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/b2b/login');
  };

  const handleSelectBusinessType = (typeId) => {
    navigate(`/b2b/order/${typeId}`);
  };

  // Auto-redirect to preferred business type if set
  React.useEffect(() => {
    if (user?.businessType) {
      // Use saved business type from registration
      navigate(`/b2b/order/${user.businessType}`);
    }
  }, [user, navigate]);

  if (!user) return null;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom, #f5f5f5, #ffffff)' }}>
      {/* Header */}
      <header className="shadow-lg" style={{ background: 'linear-gradient(90deg, #1a5c1a 0%, #2d7a2d 25%, #3d8b3d 50%, #2d7a2d 75%, #1a5c1a 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={bakeryInfo.logo} 
                alt="Dura Alsham"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-white">{bakeryInfo.name}</h1>
                <p className="text-sm text-white/80">منصة طلبات الجملة B2B</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              className="bg-cream-100 text-burgundy-800 hover:bg-cream-200"
            >
              <LogOut className="w-4 h-4 ml-2" />
              تسجيل الخروج
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">مرحباً، {user.fullName}</h2>
              <p className="text-gray-600">{user.businessName}</p>
            </div>
          </div>
        </div>

        {/* Business Type Selection */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4">اختر نوع الطلب</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {businessTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => handleSelectBusinessType(type.id)}
                className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all p-6 text-center group border-2 border-transparent hover:border-green-500"
              >
                <div className="text-6xl mb-4">{type.icon}</div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">{type.name}</h4>
                <p className="text-sm text-gray-500">{type.nameEn}</p>
                <div className="mt-4">
                  <span className="text-green-600 font-semibold group-hover:underline">
                    ابدأ الطلب ←
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/b2b/orders')}
            className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all p-6 text-right group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <History className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-gray-900">سجل الطلبات</h4>
                <p className="text-sm text-gray-500">عرض جميع طلباتك السابقة</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/b2b/reports')}
            className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all p-6 text-right group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Package className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-gray-900">التقارير</h4>
                <p className="text-sm text-gray-500">ملخص طلباتك ومشترياتك</p>
              </div>
            </div>
          </button>
        </div>
      </main>
    </div>
  );
};

export default B2BDashboard;